import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { subscriptionAPI } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, CheckCircle, Crown, Copy, XCircle } from "lucide-react";

export default function PayPalSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("processing"); // processing, success, error
  const [unlockCode, setUnlockCode] = useState(null);

  useEffect(() => {
    const paymentId = searchParams.get("paymentId");
    const payerId = searchParams.get("PayerID");
    
    if (paymentId && payerId) {
      executePayment(paymentId, payerId);
    } else {
      setStatus("error");
      toast.error("Invalid PayPal return parameters");
    }
  }, [searchParams]);

  const executePayment = async (paymentId, payerId) => {
    try {
      const { data } = await subscriptionAPI.executePayPal(paymentId, payerId);
      
      if (data.payment_status === "paid") {
        setStatus("success");
        setUnlockCode(data.unlock_code);
        toast.success(data.message || "Pro subscription activated!");
      } else {
        setStatus("error");
        toast.error("Payment was not completed");
      }
    } catch (error) {
      console.error("PayPal execution error:", error);
      setStatus("error");
      toast.error(error.response?.data?.detail || "Failed to process payment");
    }
  };

  const copyCode = () => {
    if (unlockCode) {
      navigator.clipboard.writeText(unlockCode);
      toast.success("Unlock code copied to clipboard!");
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-zinc-900/50 border-zinc-800">
        <CardContent className="pt-8 pb-6 text-center space-y-6">
          {status === "processing" && (
            <>
              <Loader2 className="h-16 w-16 animate-spin text-blue-500 mx-auto" />
              <div>
                <h2 className="text-xl font-semibold text-white">Processing PayPal Payment</h2>
                <p className="text-zinc-400 mt-2">Please wait while we confirm your payment...</p>
              </div>
            </>
          )}

          {status === "success" && (
            <>
              <div className="relative">
                <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
                <Crown className="h-8 w-8 text-amber-500 absolute top-0 right-1/3 -translate-y-2" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white">Welcome to Pro!</h2>
                <p className="text-zinc-400 mt-2">Your PayPal payment was successful.</p>
              </div>

              {unlockCode && (
                <div className="bg-zinc-800 rounded-lg p-4 space-y-2">
                  <p className="text-sm text-zinc-400">Your Unlock Code (save this!)</p>
                  <div className="flex items-center justify-center gap-2">
                    <code className="text-amber-500 font-mono text-lg">{unlockCode}</code>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={copyCode}
                      className="h-8 w-8 p-0"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}

              <Button
                onClick={() => navigate("/")}
                className="bg-amber-500 hover:bg-amber-600 text-black font-semibold"
              >
                Go to Garage
              </Button>
            </>
          )}

          {status === "error" && (
            <>
              <XCircle className="h-16 w-16 text-red-500 mx-auto" />
              <div>
                <h2 className="text-xl font-semibold text-white">Payment Issue</h2>
                <p className="text-zinc-400 mt-2">
                  There was a problem processing your PayPal payment. Please try again or contact support.
                </p>
              </div>
              <div className="flex gap-3 justify-center">
                <Button
                  variant="outline"
                  onClick={() => navigate("/subscription")}
                  className="border-zinc-700"
                >
                  Try Again
                </Button>
                <Button
                  onClick={() => navigate("/")}
                  className="bg-amber-500 hover:bg-amber-600 text-black"
                >
                  Go to Garage
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
